import React, { ReactNode, useLayoutEffect, useState } from "react";

import { useLocation } from "react-router";
import styles from "./AppBaseLayout.module.css";
import { useAppRouteGroup, useAppRouteParams } from "./AppRoutes";
import { AppMessageBar } from "./components/AppMessageBar";
import { AppNotifications } from "./components/AppNotifications";
import { useAppAccount } from "./components/AppProvider/AppAccountProvider";
import { AppSideNavGlobal } from "./components/AppSideNav/AppSideNavGlobal";
import { AppSideNavLocal } from "./components/AppSideNav/AppSideNavLocal";
import {
  AppSideNavSettings,
  isSettingsSideNavRouteGroup,
} from "./components/AppSideNav/AppSideNavSettings";
import { AppSideNavOpenStateProvider } from "./components/AppSideNav/providers/AppSideNavOpenStateProvider";
import { AppTopNav, AppTopNavSlotProvider } from "./components/AppTopNav";
import { ErrorWrapper } from "./components/ErrorWrapper";
import { useCachedOrganization } from "./components/InOrganizationProvider/OrganizationProvider";
import { useWriteVersionPresenceSession } from "./components/InOrganizationProvider/SessionProvider/hooks";
import { OrgUnderMaintenance } from "./components/OrgUnderMaintenance";
import { Organization } from "./domain/Organization";
import { useIsHeaderOnlyLayout } from "./hooks/useIsHeaderOnlyLayout";

const SERVICE_STATUS_MSG: {
  [key: string]: {
    title: string;
    desc: string;
  };
} = {
  under_maintenance: {
    title: "メンテナンス中",
    desc: "AAメンバー以外は閲覧できないように制限されています",
  },
};

export const AppBaseLayout: React.FC<{
  children: ReactNode;
  isVisibleSideNav?: boolean;
  isAdminMode?: boolean;
}> = ({ children, isVisibleSideNav = true, isAdminMode = false }) => {
  /**
   * @description 各URLで必須で適用されるコンポーネント.AppRoutesで利用
   * @param children: PageSheet、PageOrganizationTopなど各ページの主要コンポーネント
   */
  const { organizationId, versionId } = useAppRouteParams();
  const { isAAMemberOrAbove, isLoadedAccount } = useAppAccount();
  const { listeningOrganization, isLoadedOrganization } =
    useCachedOrganization();

  useWriteVersionPresenceSession(versionId);

  const location = useLocation();
  const isInOrganization = location.pathname.match(/^\/organizations\/[^/]+/);

  const isSidebarDisabled = useIsHeaderOnlyLayout();
  const isHeaderOnly = !isAdminMode && !!isInOrganization && isSidebarDisabled;
  const routeGroup = useAppRouteGroup();
  const isSettingsSideNavVisible =
    isHeaderOnly && isSettingsSideNavRouteGroup(routeGroup);
  const [
    { activeServiceStatus, activeServiceStatusOrgId },
    setActiveServiceStatus,
  ] = useState<{
    activeServiceStatus: Organization["serviceStatus"];
    activeServiceStatusOrgId: string | null;
  }>({
    activeServiceStatus: listeningOrganization?.serviceStatus ?? null,
    activeServiceStatusOrgId: organizationId,
  });
  useLayoutEffect(() => {
    const prevOrgId = activeServiceStatusOrgId;
    const nextOrgId = organizationId ?? null;
    const prevServiceStatus = activeServiceStatus;
    const nextServiceStatus = listeningOrganization?.serviceStatus ?? null;
    const newState = {
      activeServiceStatus: nextServiceStatus,
      activeServiceStatusOrgId: nextOrgId,
    };
    if (
      prevOrgId === nextOrgId &&
      prevServiceStatus === "under_maintenance" &&
      nextServiceStatus !== "under_maintenance" &&
      !isAAMemberOrAbove
    ) {
      const randomDelayToAvoidAccessSpike = 1000 * 20 * Math.random();
      const timeout = setTimeout(() => {
        window.location.reload();
      }, randomDelayToAvoidAccessSpike);
      return () => clearTimeout(timeout);
    }
    setActiveServiceStatus(newState);
  }, [organizationId, listeningOrganization?.serviceStatus]);
  const showServiceStatus = !!activeServiceStatus;
  if (isInOrganization && (!isLoadedOrganization || !isLoadedAccount)) return;
  if (!isAAMemberOrAbove && activeServiceStatus === "under_maintenance") {
    return (
      <div className={styles.appBody}>
        <div style={{ flex: 1 }}>
          <OrgUnderMaintenance />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.appLayout}>
      {showServiceStatus ? (
        <div className={styles.printHidden}>
          <AppMessageBar
            intent="warning"
            title={SERVICE_STATUS_MSG[activeServiceStatus!]?.title}
          >
            {SERVICE_STATUS_MSG[activeServiceStatus!]?.desc}
          </AppMessageBar>
        </div>
      ) : null}
      <AppSideNavOpenStateProvider
        isAdminMode={isAdminMode}
        isHeaderOnly={isHeaderOnly}
        isSettingsSideNav={isSettingsSideNavVisible}
        isVisibleSideNav={isVisibleSideNav}
      >
        <AppTopNavSlotProvider>
          <div className={styles.appBody}>
            {isVisibleSideNav && !isHeaderOnly ? (
              <>
                <AppSideNavGlobal isAdminMode={isAdminMode} />
                <AppSideNavLocal />
              </>
            ) : isSettingsSideNavVisible ? (
              <AppSideNavSettings />
            ) : null}
            <div className={styles.mainContent}>
              <AppTopNav
                showOrganizationMenu={
                  (!isVisibleSideNav || isHeaderOnly) &&
                  !isSettingsSideNavVisible
                }
                showSessionFacepile={!isHeaderOnly}
              />
              <div className={styles.scrollArea}>
                <ErrorWrapper>{children}</ErrorWrapper>
              </div>
            </div>
            <AppNotifications />
          </div>
        </AppTopNavSlotProvider>
      </AppSideNavOpenStateProvider>
    </div>
  );
};
